/**
 * Card 324 — the questionnaire builder's working copy, as pure functions.
 *
 * The builder edits a "draft": the questionnaire the backend stores, plus
 * nothing else. Every edit is a pure function over it so the rules (one
 * overall question, recommend only on yes/no, labels in every language) can be
 * tested without rendering, and so the screen stays a thin shell.
 *
 * The backend is the authority: normaliseQuestionnaireInput in TA-Ticketing's
 * questionnaire model re-validates everything. The checks here exist to tell
 * the admin what is wrong before they press Save, in words, next to the field.
 */

export const QUESTION_TYPES = [
  { value: 'rating5', label: 'Rating (Very poor to Very good)' },
  { value: 'yesno', label: 'Yes or no' },
  { value: 'single', label: 'Choose one' },
  { value: 'multi', label: 'Choose any' },
  { value: 'longText', label: 'Comments' },
  { value: 'name', label: 'Guest name' },
  { value: 'email', label: 'Guest email' }
];

export const typeLabel = type => (QUESTION_TYPES.find(t => t.value === type) || {}).label || type;

let counter = 0;
/** Short readable ids; the backend keeps any id matching [A-Za-z0-9_-]{1,40}. */
export const newId = prefix => {
  counter += 1;
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}${Math.floor(
    Math.random() * 1296
  ).toString(36)}`;
};

/**
 * Andy, 2026-10-02: the builder is English only for now ("a bit much to
 * expect our users to speak French and Irish"). The French strings below stay
 * as written so French can come back, but nothing is saved in French: every
 * payload is English only (EDITOR_LOCALES).
 */
export const EDITOR_LOCALES = ['en'];

const text = (en = '', fr = '') => ({ en, fr });

export const newQuestion = (type = 'rating5') => {
  const question = { id: newId('q'), type, label: text() };
  if (type === 'single' || type === 'multi') {
    question.options = [{ id: newId('o'), label: text() }, { id: newId('o'), label: text() }];
  }
  return question;
};

export const newSection = () => ({ id: newId('s'), title: text(), questions: [newQuestion()] });

export const newItineraryItem = (kind = 'hotel') => ({
  id: newId('i'),
  kind,
  name: '',
  place: '',
  service: text()
});

export const blankDraft = () => ({
  name: '',
  locales: EDITOR_LOCALES,
  intro: text(),
  thankYou: text(),
  sections: [
    {
      id: newId('s'),
      title: text(),
      questions: [
        {
          ...newQuestion('rating5'),
          overall: true,
          label: text(
            'Overall, how was your tour?',
            'Dans l’ensemble, comment était votre circuit?'
          )
        }
      ]
    }
  ],
  tours: []
});

/**
 * A sensible starting form, drawn from the shape of RIT's paper form without
 * naming anyone: the questions most tour operators ask, in both languages, for
 * the admin to reword rather than write from nothing.
 */
export const standardDraft = () => {
  const rating = (en, fr, extra) => ({ ...newQuestion('rating5'), label: text(en, fr), ...extra });
  return {
    name: 'Guest feedback',
    locales: EDITOR_LOCALES,
    intro: text(),
    thankYou: text(),
    sections: [
      {
        id: newId('s'),
        title: text('Your level of satisfaction', 'Votre niveau de satisfaction'),
        questions: [
          rating(
            'Overall, how satisfied were you with the tour?',
            'Dans l’ensemble, êtes-vous satisfait du circuit?',
            {
              overall: true
            }
          ),
          rating('Your Tour Manager', 'Votre accompagnateur'),
          rating('Clarity and contents of the brochure', 'Clarté et contenu de la brochure'),
          rating(
            'Content and quality of travel documents',
            'Contenu et qualité des documents de voyage'
          )
        ]
      },
      {
        id: newId('s'),
        title: text('About your trip', 'À propos de votre voyage'),
        questions: [
          {
            ...newQuestion('yesno'),
            label: text('Have you travelled with us before?', 'Avez-vous déjà voyagé avec nous?')
          },
          {
            ...newQuestion('yesno'),
            recommend: true,
            label: text(
              'Would you recommend us to family and friends?',
              'Nous recommanderiez-vous à votre famille et à vos amis?'
            )
          }
        ]
      },
      {
        id: newId('s'),
        title: text(),
        questions: [
          { ...newQuestion('longText'), label: text('Your comments', 'Vos commentaires') },
          { ...newQuestion('name'), label: text('Your name', 'Votre nom') },
          { ...newQuestion('email'), label: text('Your email', 'Votre courriel') }
        ]
      }
    ],
    tours: []
  };
};

/** A saved questionnaire from the API, as an editable draft. */
export const fromQuestionnaire = q => ({
  id: q._id,
  status: q.status,
  name: q.name || '',
  locales: EDITOR_LOCALES,
  // A form saved before French was removed: saving it now makes it English
  // only, and the editor says so first.
  hadFrench: Boolean(q.locales && q.locales.includes('fr')),
  intro: { en: '', fr: '', ...(q.intro || {}) },
  thankYou: { en: '', fr: '', ...(q.thankYou || {}) },
  sections: (q.sections || []).map(section => ({
    ...section,
    title: { en: '', fr: '', ...(section.title || {}) },
    questions: (section.questions || []).map(question => ({
      ...question,
      label: { en: '', fr: '', ...(question.label || {}) },
      options: question.options
        ? question.options.map(o => ({ ...o, label: { en: '', fr: '', ...(o.label || {}) } }))
        : undefined
    }))
  })),
  tours: (q.tours || []).map(t => ({
    tourId: String(t.tourId),
    itinerary: (t.itinerary || []).map(item => ({
      place: '',
      ...item,
      service: { en: '', fr: '', ...(item.service || {}) }
    }))
  }))
});

/** "Duplicate": same content, fresh ids, no tours (a tour can be on one live form). */
export const duplicateDraft = draft => {
  const copy = JSON.parse(JSON.stringify(draft));
  delete copy.id;
  delete copy.status;
  copy.name = `${draft.name} (copy)`;
  copy.tours = [];
  copy.sections = copy.sections.map(section => ({
    ...section,
    id: newId('s'),
    questions: section.questions.map(question => ({
      ...question,
      id: newId('q'),
      options: question.options ? question.options.map(o => ({ ...o, id: newId('o') })) : undefined
    }))
  }));
  return copy;
};

// --- edits ------------------------------------------------------------------

const move = (list, index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
};

export const moveSection = (draft, index, delta) => ({
  ...draft,
  sections: move(draft.sections, index, delta)
});

export const updateSection = (draft, sectionId, patch) => ({
  ...draft,
  sections: draft.sections.map(s => (s.id === sectionId ? { ...s, ...patch } : s))
});

export const removeSection = (draft, sectionId) => ({
  ...draft,
  sections: draft.sections.filter(s => s.id !== sectionId)
});

export const addSection = draft => ({ ...draft, sections: [...draft.sections, newSection()] });

const mapQuestions = (draft, fn) => ({
  ...draft,
  sections: draft.sections.map(section => ({
    ...section,
    questions: fn(section.questions, section)
  }))
});

export const addQuestion = (draft, sectionId, type) =>
  mapQuestions(draft, (questions, section) =>
    section.id === sectionId ? [...questions, newQuestion(type)] : questions
  );

export const removeQuestion = (draft, questionId) =>
  mapQuestions(draft, questions => questions.filter(q => q.id !== questionId));

export const moveQuestion = (draft, sectionId, index, delta) =>
  mapQuestions(draft, (questions, section) =>
    section.id === sectionId ? move(questions, index, delta) : questions
  );

/**
 * Change a question. Two rules ride along so the admin cannot build a draft the
 * backend will refuse: marking one question Overall unmarks every other, and
 * changing a question's type drops markers and options that no longer fit.
 */
export const updateQuestion = (draft, questionId, patch) => {
  let next = mapQuestions(draft, questions =>
    questions.map(q => {
      if (q.id !== questionId) return q;
      const updated = { ...q, ...patch };
      if (patch.type && patch.type !== q.type) {
        if (updated.type !== 'rating5') delete updated.overall;
        if (updated.type !== 'yesno') delete updated.recommend;
        if (updated.type === 'single' || updated.type === 'multi') {
          if (!updated.options || updated.options.length < 2)
            updated.options = newQuestion(updated.type).options;
        } else {
          delete updated.options;
        }
      }
      return updated;
    })
  );
  if (patch.overall) {
    next = mapQuestions(next, questions =>
      questions.map(q => (q.id !== questionId && q.overall ? { ...q, overall: false } : q))
    );
  }
  if (patch.recommend) {
    next = mapQuestions(next, questions =>
      questions.map(q => (q.id !== questionId && q.recommend ? { ...q, recommend: false } : q))
    );
  }
  return next;
};

export const setTourAttached = (draft, tourId, attached) => {
  const has = draft.tours.some(t => t.tourId === tourId);
  if (attached && !has) return { ...draft, tours: [...draft.tours, { tourId, itinerary: [] }] };
  if (!attached && has) return { ...draft, tours: draft.tours.filter(t => t.tourId !== tourId) };
  return draft;
};

export const updateItinerary = (draft, tourId, fn) => ({
  ...draft,
  tours: draft.tours.map(t => (t.tourId === tourId ? { ...t, itinerary: fn(t.itinerary) } : t))
});

export const copyItinerary = (draft, fromTourId, toTourId) => {
  const source = draft.tours.find(t => t.tourId === fromTourId);
  if (!source) return draft;
  return updateItinerary(draft, toTourId, () =>
    source.itinerary.map(item => ({ ...item, id: newId('i'), service: { ...item.service } }))
  );
};

export { move as moveInList };

// --- checks -----------------------------------------------------------------

const LOCALE_NAMES = { en: 'English', fr: 'French' };

/**
 * Problems that would stop a save, in the admin's words. Mirrors the backend's
 * rules; an empty list means the backend should accept it.
 */
export const draftProblems = draft => {
  const problems = [];
  const locales = draft.locales;
  const missing = value => locales.filter(l => !String((value && value[l]) || '').trim());
  const named = list => list.map(l => LOCALE_NAMES[l] || l).join(' and ');

  if (!draft.name.trim()) problems.push('Give the questionnaire a name.');

  const questions = draft.sections.flatMap(s => s.questions);
  if (questions.length === 0) problems.push('Add at least one question.');

  const overall = questions.filter(q => q.overall && q.type === 'rating5');
  if (overall.length !== 1) problems.push('Mark one rating question as the overall score.');

  draft.sections.forEach((section, si) => {
    const title = section.title || {};
    const titled = locales.some(l => String(title[l] || '').trim());
    if (titled && missing(title).length) {
      problems.push(`Section ${si + 1}: add the title in ${named(missing(title))}.`);
    }
    section.questions.forEach((q, qi) => {
      const where = `Section ${si + 1}, question ${qi + 1}`;
      if (missing(q.label).length)
        problems.push(`${where}: add the question in ${named(missing(q.label))}.`);
      if (q.type === 'single' || q.type === 'multi') {
        if (!q.options || q.options.length < 2)
          problems.push(`${where}: add at least two choices.`);
        (q.options || []).forEach((o, oi) => {
          if (missing(o.label).length) {
            problems.push(`${where}, choice ${oi + 1}: add it in ${named(missing(o.label))}.`);
          }
        });
      }
    });
  });

  ['name', 'email'].forEach(type => {
    if (questions.filter(q => q.type === type).length > 1) {
      problems.push(
        `Only one ${type === 'name' ? 'guest name' : 'guest email'} question is allowed.`
      );
    }
  });

  draft.tours.forEach(tour => {
    tour.itinerary.forEach((item, ii) => {
      if (!item.name.trim()) problems.push(`A tour's hotel or experience ${ii + 1} needs a name.`);
    });
  });

  return problems;
};

/** The body for POST/PUT /questionnaires. Empty translations are dropped. */
export const toPayload = draft => {
  const clean = value => {
    const out = {};
    draft.locales.forEach(l => {
      const v = String((value && value[l]) || '').trim();
      if (v) out[l] = v;
    });
    return out;
  };
  return {
    name: draft.name.trim(),
    locales: draft.locales,
    intro: clean(draft.intro),
    thankYou: clean(draft.thankYou),
    sections: draft.sections.map(section => ({
      id: section.id,
      title: clean(section.title),
      questions: section.questions.map(q => {
        const question = { id: q.id, type: q.type, label: clean(q.label) };
        if (q.required) question.required = true;
        if (q.overall && q.type === 'rating5') question.overall = true;
        if (q.recommend && q.type === 'yesno') question.recommend = true;
        if (q.type === 'single' || q.type === 'multi') {
          question.options = (q.options || []).map(o => ({ id: o.id, label: clean(o.label) }));
        }
        return question;
      })
    })),
    tours: draft.tours.map(tour => ({
      tourId: tour.tourId,
      itinerary: tour.itinerary.map(item => {
        const entry = { id: item.id, kind: item.kind, name: item.name.trim() };
        if (item.place && item.place.trim()) entry.place = item.place.trim();
        const service = clean(item.service);
        if (Object.keys(service).length) entry.service = service;
        return entry;
      })
    }))
  };
};

/**
 * What a guest will go through, in order. The same grouping as the guest page
 * (TA-Tipping QuestionnairePage/flow.js buildSteps), so the admin sees the
 * screens they are building rather than a list of fields.
 */
export const guestScreens = draft => {
  const screens = ['Welcome'];
  const all = draft.sections.flatMap(s => s.questions);
  const overall = all.find(q => q.overall && q.type === 'rating5');
  const late = ['longText', 'name', 'email'];
  const sectionScreens = draft.sections
    .map(section => ({
      section,
      count: section.questions.filter(
        q => !(q.overall && q.type === 'rating5') && !late.includes(q.type)
      ).length,
      holdsOverall: overall ? section.questions.includes(overall) : false
    }))
    .filter(entry => entry.count > 0);
  const title = entry =>
    (entry.section.title && entry.section.title.en) || `${entry.count} more questions`;

  if (overall) screens.push(`Overall score: ${overall.label.en || 'untitled'}`);
  sectionScreens.filter(e => e.holdsOverall).forEach(e => screens.push(title(e)));
  const anyHotels = draft.tours.some(t => t.itinerary.some(i => i.kind === 'hotel'));
  const anyExperiences = draft.tours.some(t => t.itinerary.some(i => i.kind !== 'hotel'));
  if (anyHotels) screens.push('Hotels on their tour');
  if (anyExperiences) screens.push('Visits and experiences on their tour');
  sectionScreens.filter(e => !e.holdsOverall).forEach(e => screens.push(title(e)));
  if (all.some(q => q.type === 'longText')) screens.push('Comments');
  if (all.some(q => q.type === 'name' || q.type === 'email'))
    screens.push('Name and email (optional)');
  screens.push('Thank you');
  return screens;
};
